import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight,
  BellRing,
  FolderOpen,
  Layers,
  LayoutGrid,
  List,
  ListTodo,
  ShoppingCart,
  Store,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/common";
import BookingDetail from "@/components/BookingDetail";
import { TodayBookingsSummary } from "@/components/TodayBookingsSummary";
import { api, fmtDate, STATUSES, errMsg } from "@/lib/api";
import { PrintButton } from "@/components/PrintButton";
import { BookingQuickActions } from "@/components/BookingQuickActions";

const periodLabel = (lesson) => (lesson.periods || [lesson.period])
  .map((period) => `P${period}`)
  .join("–");

function AcceptBookingToggle({ booking, onChanged, compact = false }) {
  const [busy, setBusy] = useState(false);
  if (booking.status !== "Pending") return null;
  const accept = async (checked) => {
    if (!checked) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/status`, { status: "Accepted" });
      toast.success(`${booking.reference} accepted`);
      onChanged();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 text-sm font-medium ${compact ? "px-3 py-2" : "border-t px-4 py-2.5"}`}
      data-testid={`prep-accept-control-${booking.reference}`}
    >
      <Checkbox
        checked={false}
        disabled={busy}
        onCheckedChange={(checked) => accept(Boolean(checked))}
        data-testid={`prep-accept-checkbox-${booking.reference}`}
      />
      {busy ? "Accepting…" : "Accepted"}
    </label>
  );
}

function RequestCard({ b, onOpen, onChanged }) {
  const done = b.checklist.filter((c) => c.done).length;
  return (
    <div
      className="overflow-hidden rounded-xl border bg-card transition-transform hover:-translate-y-0.5 hover:border-primary/50"
      data-testid={`prep-request-card-${b.reference}`}
    >
      <button
        type="button"
        onClick={() => onOpen(b.id)}
        className="w-full space-y-2 p-4 text-left"
        data-testid={`prep-request-open-${b.reference}`}
      >
        <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs text-muted-foreground">{b.reference}</span><StatusBadge status={b.status} testId={`prep-status-${b.reference}`} /></div>
        <div className="font-semibold leading-snug">{b.practical.name}</div>
        <div className="text-xs text-muted-foreground">
          {fmtDate(b.lesson.date)} · {periodLabel(b.lesson)} {b.lesson.start} · {b.lesson.lab}
        </div>
        <div className="text-xs">{b.teacher_name} · {b.lesson.class_name} · <span className="font-semibold">{b.sets} sets</span></div>
        <div className="text-[11px] text-muted-foreground" data-testid={`prep-assignee-${b.reference}`}>{b.practical.subject || "—"} · {b.assigned_to ? <span className="text-foreground font-medium">{b.assigned_to.name}</span> : <span className="italic">Unassigned</span>}</div>
        <div className="flex items-center gap-2 pt-1">
          <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${b.checklist.length ? (done / b.checklist.length) * 100 : 0}%` }} /></div>
          <span className="text-[11px] text-muted-foreground">{done}/{b.checklist.length}</span>
        </div>
      </button>
      <div className="flex items-center justify-between border-t">
        <AcceptBookingToggle booking={b} onChanged={onChanged} compact />
        <BookingQuickActions
          booking={b}
          canCancel={!['Cancelled', 'Completed'].includes(b.status)}
          cancelFromPrep
          onCancelled={onChanged}
        />
      </div>
    </div>
  );
}

function RequestListRow({ b, onOpen, onChanged }) {
  const done = b.checklist.filter((item) => item.done).length;
  return (
    <div
      className="flex gap-3 border-b px-4 py-3 last:border-b-0 sm:items-center"
      data-testid={`prep-request-row-${b.reference}`}
    >
      <button
        type="button"
        onClick={() => onOpen(b.id)}
        className="grid min-w-0 flex-1 gap-3 text-left transition-colors hover:text-primary sm:grid-cols-[minmax(12rem,1.6fr)_minmax(9rem,1fr)_minmax(8rem,0.8fr)_auto] sm:items-center"
        data-testid={`prep-request-open-${b.reference}`}
      >
        <div className="min-w-0">
          <div className="font-mono text-[11px] text-muted-foreground">{b.reference}</div>
          <div className="mt-0.5 truncate text-sm font-semibold">{b.practical.name}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {b.teacher_name} · {b.lesson.class_name} · {b.sets} sets
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          <div>{fmtDate(b.lesson.date)} · {periodLabel(b.lesson)} {b.lesson.start}</div>
          <div className="mt-1">{b.lesson.lab || "No lab"}</div>
        </div>
        <div className="text-xs text-muted-foreground">
          <div>{b.practical.subject || "No subject"}</div>
          <div className="mt-1 truncate">
            {b.assigned_to?.name || "Unassigned"} · {done}/{b.checklist.length} items
          </div>
        </div>
        <div className="sm:justify-self-end">
          <StatusBadge status={b.status} testId={`prep-list-status-${b.reference}`} />
        </div>
      </button>
      <BookingQuickActions
        booking={b}
        canCancel={!['Cancelled', 'Completed'].includes(b.status)}
        cancelFromPrep
        onCancelled={onChanged}
      />
    </div>
  );
}

const Stat = ({ label, value, testId, onClick }) => (
  <button
    aria-label={`Show ${label.toLowerCase()} practicals`}
    className={[
      "rounded-xl border bg-card p-4 text-left transition-colors",
      "hover:border-primary/60 hover:bg-muted/40 focus-visible:outline-none",
      "focus-visible:ring-2 focus-visible:ring-ring",
    ].join(" ")}
    onClick={onClick}
    type="button"
    data-testid={`${testId}-button`}
  >
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-bold" data-testid={testId}>{value}</div>
  </button>
);

function FilterSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-48" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();

function LessonGroupCard({ items, onOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  const l = items[0].lesson;
  const total = items.reduce((n, b) => n + b.checklist.length, 0);
  const done = items.reduce((n, b) => n + b.checklist.filter((c) => c.done).length, 0);
  const setAll = async (status) => {
    setBusy(true);
    const results = await Promise.allSettled(items.filter((b) => b.status !== status).map((b) => api.post(`/bookings/${b.id}/status`, { status })));
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length) toast.error(`${failed.length} could not move to ${status}: ${errMsg(failed[0].reason)}`);
    else toast.success(`All practicals for this lesson set to ${status}`);
    setBusy(false);
    onChanged();
  };
  return (
    <div className="rounded-xl border-2 border-primary/30 bg-card p-4 space-y-3 sm:col-span-2 min-w-0" data-testid={`prep-lesson-group-${items[0].teacher_id}-${l.lesson_key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-primary font-semibold flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />One lesson · {items.length} practicals</div>
          <div className="font-semibold mt-1">{items[0].teacher_name} · {l.class_name}</div>
          <div className="text-xs text-muted-foreground">
            {fmtDate(l.date)} · {periodLabel(l)} {l.start} · {l.lab}
          </div>
        </div>
        <Select value="" onValueChange={setAll} disabled={busy}>
          <SelectTrigger className="w-44" data-testid={`prep-group-status-${l.lesson_key}`}><SelectValue placeholder="Set all to…" /></SelectTrigger>
          <SelectContent>{STATUSES.filter((status) => status !== "Cancelled").map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="divide-y rounded-lg border">
        {items.map((b) => {
          const d = b.checklist.filter((c) => c.done).length;
          return (
            <div key={b.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`prep-request-card-${b.reference}`}>
              <button type="button" onClick={() => onOpen(b.id)} className="flex flex-1 min-w-0 items-center gap-3 text-left hover:text-primary" data-testid={`prep-request-open-${b.reference}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.practical.name}</div>
                  <div className="text-[11px] text-muted-foreground"><span className="font-mono">{b.reference}</span> · {b.sets} sets · {d}/{b.checklist.length} items · {b.assigned_to ? b.assigned_to.name : "Unassigned"}</div>
                </div>
                <StatusBadge status={b.status} testId={`prep-status-${b.reference}`} />
              </button>
              <div className="flex shrink-0 items-center">
                <AcceptBookingToggle booking={b} onChanged={onChanged} compact />
                <BookingQuickActions
                  booking={b}
                  canCancel={!['Cancelled', 'Completed'].includes(b.status)}
                  cancelFromPrep
                  onCancelled={onChanged}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
        <span className="text-[11px] text-muted-foreground" data-testid={`prep-group-progress-${l.lesson_key}`}>{done}/{total} items for the lesson</span>
      </div>
    </div>
  );
}

const groupByLesson = (list) => {
  const m = new Map();
  list.forEach((b) => {
    const k = `${b.teacher_id}|${b.lesson.lesson_key}`;
    m.set(k, [...(m.get(k) || []), b]);
  });
  return [...m.values()];
};

function AllAlertsToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => {
    api.get("/me/request-alerts").then((r) => setOn(r.data.notify_all_requests));
  }, []);
  const change = async (v) => {
    setOn(v);
    try {
      await api.put("/me/request-alerts", { notify_all_requests: v });
      toast.success(v ? "You'll be notified about every request" : "You'll only get requests assigned to you (plus unassigned ones)");
    } catch (e) {
      setOn(!v);
      toast.error(errMsg(e));
    }
  };
  if (on === null) return null;
  return (
    <label className="flex items-center gap-2 text-sm rounded-full border px-3 py-1.5" title="Requests for lessons assigned to another technician will still reach you">
      <Switch checked={on} onCheckedChange={change} data-testid="prep-all-alerts-switch" />Notify me of every request
    </label>
  );
}

export default function PrepBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [q, setQ] = useState("");
  const [who, setWho] = useState("all");
  const [teacher, setTeacher] = useState("all");
  const [subject, setSubject] = useState("all");
  const [showCompletedHistory, setShowCompletedHistory] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("aj-prep-view") || "tiles");
  const [viewPreferenceLoaded, setViewPreferenceLoaded] = useState(false);
  const [params, setParams] = useSearchParams();
  const open = params.get("booking");
  const load = useCallback(
    () => api.get("/dashboard/technician", {
      params: { include_completed: showCompletedHistory },
    }).then((r) => setData(r.data)),
    [showCompletedHistory],
  );
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    localStorage.setItem("aj-prep-view", view);
  }, [view]);
  useEffect(() => {
    api
      .get("/me/preferences")
      .then(({ data }) => {
        if (["tiles", "list"].includes(data.prep_room_view)) {
          setView(data.prep_room_view);
        }
      })
      .finally(() => setViewPreferenceLoaded(true));
  }, []);
  useEffect(() => {
    if (!viewPreferenceLoaded) {
      return;
    }
    api.put("/me/preferences", { prep_room_view: view }).catch(() => undefined);
  }, [view, viewPreferenceLoaded]);
  if (!data) return <Page wide><p className="text-sm text-muted-foreground">Loading…</p></Page>;

  let list = showCompletedHistory
    ? data.completed_history || []
    : tab === "today"
      ? data.today
      : tab === "tomorrow"
        ? data.tomorrow
        : tab === "upcoming"
          ? data.upcoming
          : data.upcoming.filter((b) => b.status === tab);
  if (q) list = list.filter((b) => `${b.practical.name} ${b.teacher_name} ${b.lesson.lab} ${b.reference}`.toLowerCase().includes(q.toLowerCase()));
  if (who === "mine") list = list.filter((b) => b.assigned_to?.id === user.id);
  else if (who === "unassigned") list = list.filter((b) => !b.assigned_to);
  else if (who !== "all") list = list.filter((b) => b.assigned_to?.id === who);
  if (teacher !== "all") list = list.filter((b) => b.teacher_id === teacher);
  if (subject !== "all") list = list.filter((b) => b.practical.subject === subject);
  const staff = [...data.technicians];
  data.upcoming.forEach((b) => b.assigned_to && !staff.some((t) => t.id === b.assigned_to.id) && staff.push(b.assigned_to));
  const whoOptions = [{ value: "all", label: "All technicians" }, { value: "mine", label: "My workflow" }, { value: "unassigned", label: "Unassigned" },
    ...staff.filter((t) => t.id !== user.id).map((t) => ({ value: t.id, label: t.name }))];
  const teachers = new Map(data.upcoming.map((b) => [b.teacher_id, b.teacher_name]));
  const teacherOptions = [{ value: "all", label: "All teachers" }, ...[...teachers].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ value: id, label: name }))];
  const subjectOptions = [{ value: "all", label: "All subjects" }, ...uniq(["Biology", "Chemistry", "Physics", ...data.upcoming.map((b) => b.practical.subject)]).map((s) => ({ value: s, label: s }))];
  const filtered = who !== "all" || teacher !== "all" || subject !== "all" || q;
  const focusList = (nextTab) => {
    setTab(nextTab);
    setQ("");
    setWho("all");
    setTeacher("all");
    setSubject("all");
    requestAnimationFrame(() => {
      document.getElementById("prep-bookings")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  return (
    <Page wide>
      <PageHeader title="Prep Room" subtitle={`Practical requests from teachers · today is ${fmtDate(data.today_date)}`}>
        <PrintButton label="Print current bookings" testId="prep-print-button" />
        <div className="print-hidden"><AllAlertsToggle /></div>
      </PageHeader>
      <div className="print-hidden"><TodayBookingsSummary /></div>
      <div className="print-hidden grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6 mb-6" data-testid="prep-quick-access">
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-tobuy"><Link to="/tobuy"><ShoppingCart className="mr-2 h-4 w-4" />To buy</Link></Button>
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-suppliers"><Link to="/suppliers"><Store className="mr-2 h-4 w-4" />Suppliers</Link></Button>
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-documents"><Link to="/prep-documents"><FolderOpen className="mr-2 h-4 w-4" />Documents</Link></Button>
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-reminders"><Link to="/annual-reminders"><BellRing className="mr-2 h-4 w-4" />Reminders</Link></Button>
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-todos"><Link to="/my-todos"><ListTodo className="mr-2 h-4 w-4" />My to-dos</Link></Button>
        <Button asChild variant="outline" size="sm" data-testid="prep-quick-borrowed"><Link to="/borrowed-equipment"><ArrowLeftRight className="mr-2 h-4 w-4" />Borrowed</Link></Button>
      </div>
      <div className="print-hidden grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat
          label="Today"
          value={data.today.length}
          testId="stat-today"
          onClick={() => focusList("today")}
        />
        <Stat
          label="Tomorrow"
          value={data.tomorrow.length}
          testId="stat-tomorrow"
          onClick={() => focusList("tomorrow")}
        />
        <Stat
          label="New (pending)"
          value={data.counts.Pending}
          testId="stat-pending"
          onClick={() => focusList("Pending")}
        />
        <Stat
          label="Ready"
          value={data.counts.Ready}
          testId="stat-ready"
          onClick={() => focusList("Ready")}
        />
      </div>
      <div id="prep-bookings" className="min-w-0">
        <div className="space-y-4 min-w-0">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="font-semibold" data-testid="prep-bookings-heading">Bookings</h2>
            <div className="flex flex-wrap items-center gap-2">
              {!showCompletedHistory && (
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="h-auto flex-wrap">
                    <TabsTrigger value="today" data-testid="prep-tab-today">Today</TabsTrigger>
                    <TabsTrigger value="tomorrow" data-testid="prep-tab-tomorrow">Tomorrow</TabsTrigger>
                    <TabsTrigger value="upcoming" data-testid="prep-tab-upcoming">All upcoming</TabsTrigger>
                    {STATUSES.slice(0, 4).map((s) => <TabsTrigger key={s} value={s} data-testid={`prep-tab-${s.replace(" ", "-").toLowerCase()}`}>{s}</TabsTrigger>)}
                  </TabsList>
                </Tabs>
              )}
              <label
                className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm"
                data-testid="prep-completed-history-control"
              >
                <Switch
                  checked={showCompletedHistory}
                  onCheckedChange={setShowCompletedHistory}
                  data-testid="prep-completed-history-switch"
                />
                Show completed history
              </label>
              <div className="flex rounded-lg border bg-card p-0.5" data-testid="prep-view-switcher">
                <Button
                  type="button"
                  variant={view === "tiles" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  title="Tile view"
                  aria-label="Tile view"
                  aria-pressed={view === "tiles"}
                  onClick={() => setView("tiles")}
                  data-testid="prep-view-tiles-button"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant={view === "list" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  title="List view"
                  aria-label="List view"
                  aria-pressed={view === "list"}
                  onClick={() => setView("list")}
                  data-testid="prep-view-list-button"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="md:w-56" data-testid="prep-filter-input" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-center" data-testid="prep-filters">
            <FilterSelect value={whoOptions.some((o) => o.value === who) ? who : "all"} onChange={setWho} options={whoOptions} testId="prep-filter-technician" />
            <FilterSelect value={teacherOptions.some((o) => o.value === teacher) ? teacher : "all"} onChange={setTeacher} options={teacherOptions} testId="prep-filter-teacher" />
            <FilterSelect value={subject} onChange={setSubject} options={subjectOptions} testId="prep-filter-subject" />
            {filtered && <Button variant="ghost" size="sm" onClick={() => { setWho("all"); setTeacher("all"); setSubject("all"); setQ(""); }} data-testid="prep-filter-clear">Clear filters</Button>}
            <span className="text-xs text-muted-foreground sm:ml-auto" data-testid="prep-filter-count">{list.length} request{list.length === 1 ? "" : "s"}</span>
          </div>
          {list.length === 0 ? (
            <Empty>{showCompletedHistory ? "No completed bookings yet." : "No requests here."}</Empty>
          ) : (
            view === "tiles" ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="prep-request-tiles">
                {groupByLesson(list).map((group) => (group.length === 1
                  ? <RequestCard key={group[0].id} b={group[0]} onOpen={(id) => setParams({ booking: id })} onChanged={load} />
                  : <LessonGroupCard key={group[0].id} items={group} onOpen={(id) => setParams({ booking: id })} onChanged={load} />))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border bg-card" data-testid="prep-request-list">
                {list.map((booking) => (
                  <RequestListRow
                    key={booking.id}
                    b={booking}
                    onOpen={(id) => setParams({ booking: id })}
                    onChanged={load}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
      <BookingDetail bookingId={open} onClose={() => setParams({})} mode="tech" onChanged={load} technicians={data.technicians} />
    </Page>
  );
}
